import { useEffect, useState } from 'react'
import type { RouterOutput } from '@/config/trpc'

type Moodboard = RouterOutput['moodboard']['getAll'][number]

/**
 * Whether the home prompt can generate a post that uses the brand's moodboard,
 * and what to tell the user when it cannot.
 *
 * - `loading`: moodboards (or the auto-selection) not settled yet. Submitting
 *   now would send the post without a moodboard, so it is blocked.
 * - `analyzing`: the only usable board is still generating its brief, typically
 *   straight out of onboarding. Blocked until it is ready; the list is polled.
 * - `stalled`: analyzing for longer than any real run. The brief is streamed by
 *   the browser that started it, so a closed tab leaves the board in
 *   `analyzing` for good. Not blocked: the user must not be trapped.
 * - `no-brief`: boards exist but none has a brief (draft, or a failed run).
 * - `none`: no moodboard at all.
 */
export type MoodboardGate =
  | { kind: 'ready' }
  | { kind: 'loading' }
  | { kind: 'analyzing'; moodboardId: number }
  | { kind: 'stalled'; moodboardId: number }
  | { kind: 'no-brief'; moodboardId: number }
  | { kind: 'none' }

// Briefs take about a minute; three is well past any healthy run.
const STALL_AFTER_MS = 3 * 60_000

export function useMoodboardGate(opts: {
  moodboards: Moodboard[] | undefined
  selectedMoodboard: Moodboard | null
  isResolving: boolean
}): { gate: MoodboardGate; blocksSubmit: boolean } {
  const { moodboards, selectedMoodboard, isResolving } = opts
  // By id: every poll returns a fresh object, which would restart the timer.
  const analyzingId = moodboards?.find((m) => m.status === 'analyzing')?.id

  // The DB does not record when analysis started, so time it from when this
  // page first saw it. Good enough to offer a way out of a dead stream.
  const [mountedAt] = useState(() => Date.now())
  const [now, setNow] = useState(mountedAt)
  useEffect(() => {
    if (analyzingId === undefined) return
    const timer = setInterval(() => setNow(Date.now()), 10_000)
    return () => clearInterval(timer)
  }, [analyzingId])

  const gate = resolveGate(moodboards, selectedMoodboard, isResolving, now - mountedAt)
  return { gate, blocksSubmit: gate.kind === 'loading' || gate.kind === 'analyzing' }
}

function resolveGate(
  moodboards: Moodboard[] | undefined,
  selected: Moodboard | null,
  isResolving: boolean,
  watchedMs: number,
): MoodboardGate {
  if (isResolving || !moodboards) return { kind: 'loading' }
  if (selected) return { kind: 'ready' }
  // A ready board exists but the auto-select effect has not run yet.
  if (moodboards.some((m) => m.status === 'ready')) return { kind: 'loading' }
  if (moodboards.length === 0) return { kind: 'none' }

  const latest = [...moodboards].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  )[0]!
  const analyzing = moodboards.find((m) => m.status === 'analyzing')
  if (analyzing) {
    return watchedMs > STALL_AFTER_MS
      ? { kind: 'stalled', moodboardId: analyzing.id }
      : { kind: 'analyzing', moodboardId: analyzing.id }
  }
  return { kind: 'no-brief', moodboardId: latest.id }
}

/** Poll the moodboard list while a brief is generating, so home picks the
 *  board up the moment it is ready instead of on the next manual refresh. */
export const pollWhileAnalyzing = (query: { state: { data?: Moodboard[] } }) =>
  query.state.data?.some((m) => m.status === 'analyzing') ? 3000 : false
